<template>
  <div class="app-customizer">
    <!-- Mode -->
    <div class="option__wrapper">
      <div class="option__label">{{ $t("theme.dark_mode") }}</div>
      <v-switch inset v-model="isDark" hide-details :label="$t('theme.enable')" @change="trackThemeChange"></v-switch>
    </div>
    <!-- Content width -->
    <div class="option__wrapper">
      <div class="option__label">{{ $t("theme.content_width") }}</div>
      <v-radio-group v-model="appContentWidth" class="mt-1" hide-details row>
        <v-radio
          v-for="option in appContentWidthOptions"
          :key="option.value"
          :label="option.label"
          :value="option.value"></v-radio>
      </v-radio-group>
    </div>

    <!-- Collapse menu -->
    <div class="option__wrapper" v-if="appContentLayoutNav === 'vertical'">
      <div class="option__label">{{ $t("theme.collapse_menu") }}</div>
      <v-switch inset v-model="menuIsVerticalNavMini" hide-details :label="'Collapse'"></v-switch>
    </div>
  </div>
</template>

<script>
  import { computed, ref } from "@vue/composition-api";
  import useAppConfig from "@core/@app-config/useAppConfig";

  import { mdiCog, mdiClose } from "@mdi/js";

  import themeVariants from "./themeVariants";
  import { mixpanelWrapper } from "@/@core/libs/mixpanel";
  import store from "@/store";

  // 3rd Party

  export default {
    setup() {
      const {
        appContentLayoutNav,
        appSkinVariant,
        appContentWidth,
        appRouteTransition,
        menuIsVerticalNavMini,
        menuIsMenuHidden,
        appBarType,
        appBarIsBlurred,
        footerType,
        isDark,
        isRtl,
        themes,
      } = useAppConfig();

      const selectedTheme = ref("vuexy");

      const routerTransitionOptions = [
        {
          text: "Scroll X",
          value: "scroll-x-transition",
        },
        {
          text: "Scroll X Reverse",
          value: "scroll-x-reverse-transition",
        },
        {
          text: "Scroll Y",
          value: "scroll-y-transition",
        },
        {
          text: "Scroll Y Reverse",
          value: "scroll-y-reverse-transition",
        },
        {
          text: "Slide X",
          value: "slide-x-transition",
        },
        {
          text: "Slide X Reverse",
          value: "slide-x-reverse-transition",
        },
        {
          text: "Slide Y",
          value: "slide-y-transition",
        },
        {
          text: "Slide Y Reverse",
          value: "slide-y-reverse-transition",
        },
        {
          text: "Fade",
          value: "fade-transition",
        },
      ];

      const skinOptions = computed(() => {
        const options = [
          {
            label: "Default",
            value: "default",
          },
          {
            label: "Bordered",
            value: "bordered",
          },
          {
            label: "Semi Dark",
            value: "semi-dark",
          },
        ];
        if (appContentLayoutNav.value === "horizontal") options.splice(-1, 1);

        return options;
      });

      const appContentWidthOptions = [
        {
          label: "Full Width",
          value: "full",
        },
        {
          label: "Boxed",
          value: "boxed",
        },
      ];

      const appBarTypeOptions = computed(() => {
        const types = [
          {
            label: "Fixed",
            value: "fixed",
          },
          {
            label: "Static",
            value: "static",
          },
          {
            label: "Hidden",
            value: "hidden",
          },
        ];
        if (appContentLayoutNav.value === "horizontal") types.splice(-1, 1);

        return types;
      });

      const footerTypeOptions = [
        {
          label: "Fixed",
          value: "fixed",
        },
        {
          label: "Static",
          value: "static",
        },
        {
          label: "Hidden",
          value: "hidden",
        },
      ];

      const appContentLayoutNavOptions = [
        {
          label: "Vertical",
          value: "vertical",
        },
        {
          label: "Horizontal",
          value: "horizontal",
        },
      ];

      const perfectScrollbarOptions = {
        maxScrollbarLength: 60,
        wheelPropagation: false,
      };

      return {
        selectedTheme,
        appRouteTransition,
        themeVariants,

        // App Content Layout Nav
        appContentLayoutNavOptions,
        appContentLayoutNav,

        // Skin
        appSkinVariant,
        skinOptions,

        // App Content Width
        appContentWidth,
        appContentWidthOptions,

        menuIsMenuHidden,
        menuIsVerticalNavMini,

        // AppBar Type
        appBarType,
        appBarIsBlurred,
        appBarTypeOptions,

        // Footer Type
        footerType,
        footerTypeOptions,

        isDark,
        isRtl,
        themes,
        routerTransitionOptions,

        // Perfect scrollbar options
        perfectScrollbarOptions,

        // Icons
        icons: {
          mdiCog,
          mdiClose,
        },
      };
    },
    methods: {
      trackThemeChange() {
        mixpanelWrapper.track("Theme Switched", { previous_page: store.getters["getPreviousRoute"] });
      },
    },
  };
</script>

<style lang="scss">
  @import "~vuetify/src/styles/styles.sass";
  @import "@/@core/preset/preset/functions.scss";
  @import "@/@core/preset/preset/typography/index.scss";

  .ps-customizer {
    height: calc(100% - 81px) !important;
  }

  .app-customizer-toggler {
    position: fixed;
    top: 50%;
    transform: translateX(-50%);
    background: var(--v-primary-base);
    @include ltr() {
      right: -22px;
    }
    @include rtl() {
      left: 20px;
    }
  }

  @include theme(app-customizer-drawer) using ($material) {
    background-color: map-deep-get($material, "cards");
  }

  .app-customizer {
    z-index: 7;

    .option {
      &__wrapper {
        &:not(:last-of-type) {
          margin-bottom: 24px;
        }
      }
      &__label {
        @include text-body-2-bold();
        color: get-color("subtitle") !important;
        margin-bottom: 12px;
      }
    }

    .v-label {
      @include text-body-2();
      color: get-color("body") !important;
    }
    .v-input--selection-controls__input {
      margin-right: 8px !important;
    }

    .v-radio {
      &:not(.v-item--active) {
        .v-icon {
          color: get-color("icon") !important;
        }
      }
    }

    .app-customizer-header {
      position: relative;
      .icon-customizer-close {
        position: absolute;
        @include ltr() {
          right: 20px;
        }
        @include rtl() {
          left: 20px;
        }
        top: 50%;
        transform: translateY(-50%);
      }
    }

    // Fixes Overlay is shown below SystemBar
    @at-root {
      .v-overlay {
        & + .v-application--wrap > .v-system-bar {
          z-index: 6 !important;
        }
      }
    }
  }
</style>
